; (function (root, factory) {
    if (typeof define === "function" && define.amd) {
        define([], function () {
            return (root.DateSlider = factory());
        });
    } else {
        root.DateSlider = factory();
    }
})(window, function () {
    "use strict";

    var ariaPrefix = "aria";
    var beforeend = "beforeend";
    var culture = document.documentElement.lang || "en";
    var dateSliderPrefix = "date-slider";
    var day = "day";
    var pixel = "px";
    var role = "role";
    var selectedWord = "selected";
    var available = "available";
    var transitionDuration = "transitionDuration";
    var numberDayNotAvailable = 1;
    var queryAlertNotAvailable = "[data-alert-not-available]";
    var ariaSelected = ariaAttribute(selectedWord);
    var ariaHidden = ariaAttribute("hidden");
    var dateSliderDay = dateSliderAttribute(day);
    var dateSliderSelected = dateSliderAttribute(selectedWord);
    var dateAvailable = dateSliderAttribute(available);
    var selectedAttributes = [dateSliderSelected, ariaSelected];
    var atWord = culture === "en" ? "at" : "al";

    /*
     * Default configuration constants.
     */
    var configuration = {
        day: 86400000,
        dayAsNumberFormatter: new Intl.DateTimeFormat(culture, { day: "numeric" }),
        dayFormatter: new Intl.DateTimeFormat(culture, { weekday: "short" }),
        monthFormatter: new Intl.DateTimeFormat(culture, { month: "long" }),
        monthAsNumberFormatter: new Intl.DateTimeFormat(culture, { month: "numeric" }),
        yearFormatter: new Intl.DateTimeFormat('en', { year: "numeric" })
    };

    /**
     * HTML where a given day is displayed.
     */
    var cardTemplate =
        "<button " +
        dateSliderDay +
        dateSliderSelected +
        ariaHidden +
        ariaSelected +
        dateAvailable +
        role +
        "='option' class='days__item' type='button' style='width: " +
        pixel +
        "'>" +
        "title<span>name</span>" +
        "<p>number</p>" +
        "</button>";

    var titleTemplate = "<span class='title__button'>titleButton</span>";

    /**
     * Default properties of the date slider object.
     */
    var dateSliderConfiguration = {
        cardOutside: NaN,
        cardSize: NaN,
        container: undefined,
        firstDay: NaN,
        index: 0,
        isClick: false,
        month: undefined,
        totalCards: 0,
        touchStart: NaN,
        touchNow: NaN,
        x: 0,
        noAvailable: undefined
    };

    /**
     * public
     * Watch the element until it's visible, and recalculates the styles.
     */
    let watchForStylesRecalculation = function (datePicker) {
        let config = {
            root: datePicker.config.container.parentElement
        };

        /**
         * Watch for the visibility of the container, we need to do this because initializing a Date Slider
         * While it's hidden causes a lot of issues with the component
         */
        let observer = new IntersectionObserver(function (entries, intersectionObserver) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                    datePicker.recalculateSize();
                }
            });
        }, config);

        let element = datePicker.config.container;

        observer.observe(element);
    }

    /**
     * Constructor of the dateSlider component
     * Uses Date format instead of milliseconds for all the date manipulations to avoid Daylight Saving issues.
     * @param {object} parameters A json object with the parameters to initialize the dateSlider
     * @param {string} [parameters.firstDate] - The **start date for the slider**, provided as an **ISO 8601 formatted string** (e.g., `"2026-01-05T00:00:00.000Z"`),
     * not as a Date object or timestamp. This date will be used as the anchor/first selectable date for the slider.
     * Passing a string is required to ensure cross-browser consistency and to avoid time zone and Daylight Saving Time (DST) issues.
     * If omitted, the current date (at midnight) will be used by default.
     * @param {string} [parameters.latestDay] - (Optional) The latest selectable date, as an ISO 8601 string
 *     (e.g., "2026-01-10T00:00:00.000Z"), or omitted (in which case today's date will be used by default).
 *     If provided, it will be converted to a Date object within the DateSlider component.
     */
    var DateSlider = function (parameters) {
        var _ = this;
        var key = undefined;
        var today = new Date();
        today.setHours(0, 0, 0, 0); // Sets the time to midnight for accurate date comparisons

        _.config = {};

        for (key in dateSliderConfiguration) {
            if (dateSliderConfiguration.hasOwnProperty(key)) {
                _.config[key] = dateSliderConfiguration[key];
            }
        }

        _.config.container = parameters.container;
        _.config.queryAlertNoAvailable = queryAlertNotAvailable;
        _.config.phone = parameters.phone !== undefined ? parameters.phone : "";
        _.config.numberDayNotAvailable = numberDayNotAvailable;

        // ToDo: make the '/' the default separator and remove the separator option from constructor
        _.config.separator = parameters.separator && parameters.separator.length === 1
            ? parameters.separator
            : '-';

        // Using Date format instead of milliseconds to parse the firstDate to avoid Daylight Saving issues
        if (parameters.firstDate) {
            var firstDate = new Date(parameters.firstDate);
            firstDate.setDate(firstDate.getDate() - 1);
            _.firstDay = firstDate;
        } else {
            _.firstDay = today;
        }

        _.latestDay = new Date(parameters.latestDay || today);

        // currentDayPointer is initialized to latestDay to avoid modifying latestDay during createDayObject calls
        _.currentDayPointer = new Date(_.latestDay);
        _.initialSelected = new Date(parameters.firstDate || today);
        _.initialTotalCards = parameters.initialTotalCards || 0;
        _.onSetDay = null;

        if (parameters.firstDate !== undefined) {
            var dayDiff = Math.floor((Date.parse(parameters.firstDate) - today) / configuration.day);
            _.config.index = dayDiff - 1;
        } else {
            _.config.index = 0;
        }

        // .firstDay holds the first (possibly disabled) card date for UI context.
        // .initialDay is always set to the active/selectable user date, for proper highlight and selection when the modal opens.
        // This distinction allows the slider to show context while correctly focusing user intent.
        _.initialDay = parameters.firstDate
            ? new Date(parameters.firstDate)
            : _.firstDay;

        if (_.config.phone) {
            var notAvailableAlertElem = _.config.container.querySelector(queryAlertNotAvailable);
            notAvailableAlertElem.innerText = notAvailableAlertElem.innerText + " " + atWord + " " + _.config.phone;
        }

        ["border", "cards", "margin"].forEach(fillAttribute.bind(_));
        ["today", "tomorrow"].forEach(fillStringAttribute.bind(_));
        ["daysContainer", "daysWrapper", "month", "next", "prev"].forEach(fillElement.bind(_));
        ["click"].forEach(event.bind(_, onClick, "daysContainer", false));
        ["click"].forEach(event.bind(_, onNext, "next", false));
        ["click"].forEach(event.bind(_, onPrevious, "prev", false));
        ["keydown"].forEach(event.bind(_, onKeyDown, "daysContainer", false));
        ["mousedown"].forEach(event.bind(_, onTouchStart, "daysContainer", false));
        ["mousemove"].forEach(event.bind(_, onTouchMove, "daysContainer", false));
        ["touchstart"].forEach(event.bind(_, onTouchStart, "daysContainer", { passive: true }));
        ["touchmove"].forEach(event.bind(_, onTouchMove, "daysContainer", { passive: true }));
        ["mouseup", "mouseleave", "touchend"].forEach(event.bind(_, onTouchEnd, "daysContainer", false));

        requestAnimationFrame(pleaseSetTheDate.bind(undefined, _));

        watchForStylesRecalculation(this);

        _.recalculateSize();

        setHidden(_);
    };

    /**
     * public
     * cardTotalSize: returns the size of the card including the margin.
     */
    DateSlider.prototype.cardTotalSize = function () {
        return this.config.cardSize + this.config.cardOutside;
    };

    /**
     * public
     * createDayCard: returns the HTML as string that represents a card with a date in the date slider.
     *
     * @param {object} card object containing the date information that is going to be displayed in the card.
     */
    DateSlider.prototype.createDayCard = function (card) {
        var _ = this;

        _.config.totalCards += 1;

        return replaceTemplate(card, cardTemplate);
    };

    /**
     * public
     * createDayObject: returns an object with the information needed to generated the card HTML.
     * Uses Date object manipulation to avoid Daylight Saving issues

     * @param {boolean} isHidden true if the card is not visible when added to the DOM, otherwise false.
     */
    DateSlider.prototype.createDayObject = function (isHidden, isAvailable) {
        var _ = this;
        // Use currentDayPointer to avoid modifying latestDay and normalize time to midnight
        var currentDay = new Date(_.currentDayPointer);
        currentDay.setHours(0, 0, 0, 0);
        var titleButton = "";
        var today = Date.parse(new Date().toDateString());

        if (currentDay.valueOf() === today) {
            titleButton = titleTemplate.replace("titleButton", _.today);
        } else {
            // Calculating tomorrow by adding one day to today's date
            var tomorrow = new Date(new Date().toDateString());
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (currentDay.valueOf() === tomorrow.valueOf()) {
                titleButton = titleTemplate.replace("titleButton", _.tomorrow);
            }
        }
        var card = {
            aria: {
                selected: false,
                hidden: isHidden
            },
            name: configuration.dayFormatter
                .format(currentDay)
                .toUpperCase()
                .replace(".", ""),
            number: currentDay.getDate(),
            px: _.config.cardSize + pixel,
            title: titleButton
        };

        card[dateSliderPrefix] = {
            day: currentDay.toISOString(), // Use toISOString to have a consistent date format
            selected: false,
            available: isAvailable
        };

        var nextDay = new Date(currentDay);
        nextDay.setDate(nextDay.getDate() + 1); // increment by one day
        _.currentDayPointer = nextDay;

        return card;
    };

    /**
     * public
     * isScrolling: returns true if a touchStart event has been triggered, otherwise false.
     */
    DateSlider.prototype.isScrolling = function () {
        return this.config.touchStart || this.config.touchStart === 0;
    };

    /**
     * public
     * recalculateSize: sets the cardOutside and cardSize values.
     *
     * @param {function} callback function to execute after the recalculation.
     */
    DateSlider.prototype.recalculateSize = function (callback) {
        var _ = this;

        requestAnimationFrame(calculateSize.bind(undefined, _, callback));
    }

    /**
     * public
     * setDay: sets the day from the given date string as selected.
     *
     * @param {string} dateString date represented as ISO string that will be used to populate the title.
     */
    DateSlider.prototype.setDay = function (dateString) {
        var dateObj = new Date(dateString);
        var milliseconds = dateObj.getTime();
        var _ = this;
        var separator = _.config.separator;
        var previousSelectedElement = undefined;
        var selectedElement = undefined;
        var date = configuration.monthAsNumberFormatter.format(milliseconds) + separator +
            configuration.dayAsNumberFormatter.format(milliseconds) + separator +
            configuration.yearFormatter.format(milliseconds);

        previousSelectedElement = searchElement(_, selectedWord + "='true'");
        selectedElement = searchElement(_, day + "='" + dateString + "'");
        selectedAttributes.forEach(
            bindAttribute.bind(previousSelectedElement, false)
        );
        selectedAttributes.forEach(bindAttribute.bind(selectedElement, true));

        if (typeof _.onSetDay === "function") {
            _.onSetDay(date);
        }
    };

    /**
     * public
     * setMonth: sets the month from the give date as title in the HTML.
     *
     * @param {date} date any date that will be used to populate the title.
     */
    DateSlider.prototype.setMonth = function (date) {
        var _ = this;
        var month = undefined;

        date = date || _.latestDay;
        month = configuration.monthFormatter.format(date);
        _.config.month = month;
        _.month.innerHTML = month;
    };

    /**
     * public
     * translate: moves the daysContainer in X axis the amount of pixels indicated.
     *
     * @param {number} translation amount of pixels to move the container in X axis.
     */
    DateSlider.prototype.translate = function (translation) {
        var _ = this;
        var third = _.config.cardSize / 3;

        _.config.x += translation;
        _.daysContainer.style["transform"] =
            "translateX(" + _.config.x + pixel + ")";

        if (Math.abs(_.config.x) > third) {
            _.prev.removeAttribute("disabled");
        } else {
            _.prev.setAttribute("disabled", "true");
        }

        tomorrowNeverKnows(_);
    };

    /**
     * public
     * updateMonth: sets the month name according to the object index.
     *
     * @param {object} _ instance that is being updated.
     */
    DateSlider.prototype.updateMonth = function () {
        var _ = this;
        var indexDate = new Date(_.initialDay);
        indexDate.setDate(indexDate.getDate() + _.config.index);

        if (configuration.monthFormatter.format(indexDate) !== _.config.month) {
            _.setMonth(indexDate);
        }
    };

    /**
     * private
     * ariaAttribute: returns the name of an attribute with the aria prefix;
     *
     * @param {string} word name of the attribute.
     */
    function ariaAttribute(word) {
        return ariaPrefix + "-" + word;
    }

    /**
     * private
     * calculateSize: calculates the value of the cardOutside and cardSize, and then updates the width of the days.
     *
     * @param {object} _ instance where the amount of cards configured are going to be added as days.
     * @param {function} callback function to execute after the recalculation.
     */
    function calculateSize(_, callback) {
        var width = _.daysWrapper.offsetWidth;
        var days = null;

        _.config.cardOutside = _.margin + _.border;
        _.config.cardSize = width / _.cards - _.config.cardOutside;

        days = [].slice.call(_.daysContainer.querySelectorAll("[date-slider-day]"));
        days.forEach(function (day) {
            day.style["width"] = _.config.cardSize + pixel;
        });

        if (typeof callback === "function") {
            callback(_);
        }
    }

    /**
     * private
     * bindAttribute: sets the value of the given attribute in the DOM element.
     *
     * @param {any} value value to be set for the attribute.
     * @param {string} attribute name of the attribute.
     */
    function bindAttribute(value, attribute) {
        var _ = this;

        if (!_) {
            return;
        }

        _.setAttribute(attribute, value);
    }

    /**
     * private
     * dateSliderAttribute: returns the name of an attribute with the date-slider prefix;
     *
     * @param {string} word name of the attribute.
     */
    function dateSliderAttribute(word) {
        return dateSliderPrefix + "-" + word;
    }

    /**
     * private
     * event: adds an event listener to the give eventName point to the given handler.
     *
     * @param {function} handler function to be executed when the event is triggered.
     * @param {string} element name of the property that references the node element that will trigger the event.
     * @param {string} eventName name of the event to be add to the current node element.
     */
    function event(handler, element, passiveOption, eventName) {
        this[element].addEventListener(eventName, handler.bind(this), passiveOption);
    }

    /**
     * private
     * fillAttribute: creates a property on the caller with the attribute from the given DOM node.
     *
     * @param {string} attribute name of the attribute without the date-slider prefix.
     */
    function fillAttribute(attribute) {
        this[attribute] = +this.config.container.getAttribute(
            dateSliderAttribute(attribute)
        );
    }

    /**
     * private
     * fillStringAttribute: creates a property on the caller with the attribute from the given DOM node.
     *
     * @param {string} attribute name of the attribute without the date-slider prefix.
     */
    function fillStringAttribute(attribute) {
        this[attribute] = this.config.container.getAttribute(
            dateSliderAttribute(attribute)
        );
    }

    /**
     * private
     * fillElement: creates a property on the caller with the DOM node that matches the given role.
     *
     * @param {string} element name of the role.
     */
    function fillElement(element) {
        this[element] = searchElement(this, role + "='" + element + "'");
    }

    /**
     * private
     * haveANiceDay: shining like a diamond, rolling with the dice.
     *
     * @param {object} _ instance where the amount of cards configured are going to be added as days.
     * @param {boolean} isHidden true if the card is not visible when append to the DOM, otherwise false.
     */
    function haveANiceDay(_, isHidden, isAvailable, paintCards) {
        var card = undefined;
        var daysHtml = "";
        var cardsCreated = undefined;
        var numberOfCards = paintCards ? paintCards : _.cards;

        for (cardsCreated = 0; cardsCreated < numberOfCards; cardsCreated += 1) {
            card = _.createDayObject(isHidden, isAvailable);
            daysHtml += _.createDayCard(card);
        }

        _.daysContainer.insertAdjacentHTML(beforeend, daysHtml);

        return cardsCreated;
    }

    /**
     * private
     * movementX: returns the clientX coordinate of the mouse or touch event.
     *
     * @param {event} event with the information of the mouse or touch event.
     */
    function movementX(event) {
        return event.changedTouches
            ? event.changedTouches[0].clientX
            : event.clientX;
    }

    /**
     * private
     * onClick: handler of the click or touch events.
     *
     * @param {event} event with the information of the mouse or touch event.
     */
    function onClick(event) {
        var _ = this;
        var filter = undefined;
        var target = undefined;

        if (!_.config.isClick) {
            return;
        }

        filter = "[" + dateSliderDay + "]";
        available = "[" + dateAvailable + "='true']";

        for (
            target = event.target;
            target && target != _.config.container;
            target = target.parentNode
        ) {
            if (target.matches(filter) && target.matches(available)) {
                _.setDay(target.getAttribute(dateSliderDay));
                break;
            }
        }
    }

    /**
      * private
      * onKeyDown: handler of the key press event.
      * 
      * @param {event} event with the key press information.
      */
    function onKeyDown(event) {
        var _ = this;
        var indexDate = undefined;
        var indexDay = undefined;

        if (!event.target.tagName.match("TEXTAREA|INPUT|SELECT")) {
            if (event.keyCode === 37) {
                onPrevious.call(_);
            } else if (event.keyCode === 39) {
                onNext.call(_);
            } else if (event.keyCode === 13) {
                var currentDate = new Date(_.firstDay);
                currentDate.setDate(currentDate.getDate() + _.config.index);
                var currentDayIso = currentDate.toISOString();
                _.setDay(currentDayIso);
            }
        }
    }

    /**
     * private
     * onNext: handler of the next button click .
     */
    function onNext() {
        var _ = this;
        var cardSize = _.cardTotalSize();
        var difference = Math.abs(_.config.x) - _.config.index * cardSize;

        var selectedElem = searchElement(_, selectedWord + "='true'");
        if (!selectedElem) {
            var availableCards = _.daysContainer.querySelectorAll(":not([dateAvailable='false'])");
            if (availableCards.length === 0) {
                return;
            }
            selectedElem = availableCards[0];
            selectedElem.setAttribute(selectedWord, "true");
        }

        var selectedDayIso = selectedElem.getAttribute(dateSliderDay);
        var move = cardSize * -1;
        var selectedDayDate = new Date(selectedDayIso);
        selectedDayDate.setDate(selectedDayDate.getDate() + 1); // Calculates the next day
        var nextDayIso = selectedDayDate.toISOString();

        var nextElem = searchElement(_, day + "='" + nextDayIso + "'");
        if (!nextElem) {
            return;
        }

        // Update selection in DOM
        selectedElem.removeAttribute(selectedWord);
        nextElem.setAttribute(selectedWord, "true");

        _.daysContainer.style[transitionDuration] = "0.3s";
        _.config.index += 1;

        // Calculate the expected position after the move and compute the difference
        var expectedX = -cardSize * _.config.index;
        var difference = expectedX - _.config.x;
        _.translate(difference);

        _.updateMonth();
        _.setDay(nextDayIso);

        setHidden(_);
    }

    /**
     * private
     * onPrevious: handler of the previous button click.
     */
    function onPrevious() {
        var _ = this;
        var cardSize = _.cardTotalSize();

        var move = cardSize;
        var selectedElem = searchElement(_, selectedWord + "='true'");
        if (!selectedElem) {
            var availableCards = _.daysContainer.querySelectorAll(":not([dateAvailable='false'])");
            if (availableCards.length === 0) {                
                return;
            }
            selectedElem = availableCards[0];
            selectedElem.setAttribute(selectedWord, "true");
        }
        var selectedDayIso = selectedElem.getAttribute(dateSliderDay);

        var selectedDayDate = new Date(selectedDayIso);
        selectedDayDate.setDate(selectedDayDate.getDate() - 1);
        var prevDayIso = selectedDayDate.toISOString();

        var prevElem = searchElement(_, day + "='" + prevDayIso + "'");
        if (!prevElem) {
            return;
        }

        // Update selection in DOM
        selectedElem.removeAttribute(selectedWord);
        prevElem.setAttribute(selectedWord, "true");

        _.daysContainer.style[transitionDuration] = "0.3s";
        _.translate(move);

        if (_.config.numberDayNotAvailable === 0 || searchElement(_, day + "='" + prevDayIso + "'").getAttribute(dateAvailable) !== "false") {
            _.config.index -= 1;
            _.updateMonth();
            _.setDay(prevDayIso);
        }

        setHidden(_);
    }

    /**
     * private
     * onTouchEnd: handler of mouse up and touch end events.
     *
     * @param {event} event with the information of the mouse or touch event.
     */
    function onTouchEnd(event) {
        var _ = this;
        var totalMovement = Math.abs(movementX(event) - _.config.touchStart);
        _.config.container.querySelector(_.config.queryAlertNoAvailable).setAttribute("hidden", "");

        _.config.isClick = totalMovement < 10;

        if (!_.isScrolling()) {
            return;
        }

        _.config.touchNow = null;
        _.config.touchStart = null;

        if (_.config.x > 0) {
            _.daysContainer.style[transitionDuration] = "0.7s";
            _.translate(-_.config.x);
            return;
        }

        _.config.index = Math.abs(Math.round(_.config.x / _.cardTotalSize()));

        setHidden(_);
    }

    /**
     * private
     * onTouchMove: handler of mouse and touch move events.
     *
     * @param {event} event with the information of the mouse or touch event.
     */
    function onTouchMove(event) {
        var _ = this;
        var clientX = undefined;
        var translation = undefined;

        if (!_.isScrolling()) {
            return;
        }

        clientX = movementX(event);
        translation = clientX - _.config.touchNow;

        _.config.touchNow = clientX;
        _.config.index = Math.abs(Math.round(_.config.x / _.cardTotalSize())) || 0;
        _.translate(translation);
        _.updateMonth();
    }

    /**
     * private
     * onMouseOver: handler of on mouse over.
     *
     * @param {event} event with the information of the mouse or touch event.
     */
    function handlerAlertNotAvailable(event) {
        var config = this;
        var element = config.container.querySelector(config.queryAlertNoAvailable);
        if (event.type === "mouseenter" || event.type === "click" || event.type === "touchend") {
            element.removeAttribute("hidden");
        }
        else {
            element.setAttribute("hidden", "");
        }
    }

    /**
     * private
     * onTouchStart: handler of mouse down and touch start events.
     *
     * @param {event} event
     */
    function onTouchStart(event) {
        var _ = this;
        var clientX = movementX(event);
        _.config.container.querySelector(_.config.queryAlertNoAvailable).setAttribute("hidden", "");
        _.daysContainer.style[transitionDuration] = "";
        _.config.touchStart = clientX;
        _.config.touchNow = clientX;
        _.config.isClick = true;
    }

    /**
     * private
     * pleaseSetTheDate: well, don't say tomorrow 'cause tomorrow is too far away.
     *
     * @param {object} _ instance setting the date.
     */
    function pleaseSetTheDate(_) {
        _.recalculateSize(function (data) {
            var todayDate = Date.parse(new Date().toDateString());

            var selectedDate = new Date(_.firstDay);
            selectedDate.setHours(0, 0, 0, 0);

            var days = Math.round(Math.abs((todayDate - selectedDate) / configuration.day));

            days += data.config.numberDayNotAvailable;
            data.translate(data.cardTotalSize() * days * -1);
        });

        if (_.config.numberDayNotAvailable > 0) {
            _.initialTotalCards -= haveANiceDay(_, true, false, _.config.numberDayNotAvailable);
        }

        _.initialTotalCards -= haveANiceDay(_, false, true);
        _.initialTotalCards -= haveANiceDay(_, true, true);

        if (_.initialTotalCards > 0) {
            _.initialTotalCards -= haveANiceDay(_, true, true, _.initialTotalCards);
        }

        var startDate = new Date(_.firstDay);
        startDate.setDate(startDate.getDate() + _.config.numberDayNotAvailable);
        _.setMonth(startDate);
        _.setDay(startDate.toISOString());
        _.config.index += _.config.numberDayNotAvailable;
        _.config.noAvailable = _.config.container.querySelector("[" + dateAvailable + "='false']");
        ["mouseenter", "click", "mouseleave", "touchend"].forEach(event.bind(_.config, handlerAlertNotAvailable, "noAvailable", false));

        setHidden(_);
    }

    /**
     * private
     * replaceTemplate: function to replace the configurable values from the cardTemplate.
     *
     * @param {object} obj values to be replaced in the template.
     * @param {string} template template to be replaced.
     * @param {string} prefix nested properties refers to their parents as a prefix.
     */
    function replaceTemplate(obj, template, prefix) {
        var attribute = undefined;
        var key = undefined;
        var value = undefined;

        prefix = prefix || "";

        for (key in obj) {
            if (typeof obj[key] === "object") {
                template = replaceTemplate(obj[key], template, key + "-");
            } else {
                attribute = prefix + key;
                value = prefix ? attribute + "='" + obj[key] + "'" : obj[key];
                template = template.replace(attribute, value);
            }
        }

        return template;
    }

    /**
     * private
     * searchAllElements: searches all the DOM nodes that belongs to the given instance and matches the given query.
     *
     * @param {object} instance where the search is going to be executed.
     * @param {string} query filter the elements by a given criteria.
     */
    function searchAllElements(instance, query) {
        var filter = dateSliderAttribute(query);
        return instance.config.container.querySelectorAll("[" + filter + "]");
    }

    /**
     * private
     * searchElement: search for the first DOM nodes that belongs to the given instance and matches the given query.
     *
     * @param {object} instance where the search is going to be executed.
     * @param {string} query filter the elements by a given criteria.
     */
    function searchElement(instance, query) {
        var filter = dateSliderAttribute(query);
        return instance.config.container.querySelector("[" + filter + "]");
    }

    /**
     * private
     * setHidden: updates the aria-hidden attribute to reflect what is visible on the screen.
     *
     * @param {object} _ instance where the aria-hidden attribute is going to be updated.
     */
    function setHidden(_) {
        var cards = [].slice.call(searchAllElements(_, day));
        var isHidden = undefined;
        var lastVisibleCard = _.config.index + _.cards;
        var x = undefined;

        for (x = 0; x < _.config.totalCards; x += 1) {
            const card = cards[x];
            isHidden = x < _.config.index || x >= lastVisibleCard;
            card.setAttribute(ariaHidden, isHidden);

            if (isHidden) {
                card.setAttribute('tabindex', '-1');
            }
            else {
                card.removeAttribute('tabindex');
            }
        }
    }

    /**
     * private
     * tomorrowNeverKnows: turn off your mind, relax and float down stream.
     *
     * @param {object} _ instance where the new days are going to be added.
     */
    function tomorrowNeverKnows(_) {
        var availableCards = _.config.totalCards - _.config.index * 2;
        var card = undefined;

        if (availableCards < _.cards) {
            card = _.createDayObject(true, true);
            _.daysContainer.insertAdjacentHTML(beforeend, _.createDayCard(card));
        }
    }

    return DateSlider;
});